/**
 * Lost een botsing tussen twee opties op in plaats van de keuze te verbergen.
 *
 * Het probleem dat dit oplost: bij hpl-trespa bestaat bedrukken alleen met
 * laminaat, en onbedrukt alleen zonder. Twee opties die elkaar wederzijds
 * uitsluiten. De configurator verwijderde uitgesloten waarden uit de lijst, en
 * bij zo'n paar geeft dat een patstelling: sta je op onbedrukt zonder
 * laminaat, dan is bedrukken onbereikbaar, en andersom net zo. De klant kon
 * daardoor alleen de onbedrukte uitvoering bestellen, terwijl de bedrukte
 * gewoon leverbaar is.
 *
 * De regel die hier uit volgt, en die breder geldt dan dit ene product:
 *
 *   Een waarde hoort kiesbaar te zijn zolang er een geldige combinatie mee
 *   bestaat. Alleen wat in geen enkele combinatie kan, hoort te verdwijnen.
 *
 * Klikt de klant op zo'n waarde, dan verandert de botsende optie mee. Dat is
 * wat een mens ook zou doen: kies je bedrukken, dan hoort het laminaat vanzelf
 * op een waarde te staan die daarbij bestaat.
 *
 * Deze module weet niets van React of van de vorm van uitsluitregels. De
 * aanroeper geeft een `isExcluded`-functie mee, zodat dezelfde logica geldt
 * voor Print.com-uitsluitingen en voor onze eigen regels.
 */

export type OptionForResolution = {
  id: string;
  hidden?: boolean;
  locked?: boolean;
  values?: Array<{ value: unknown }>;
};

export type ResolutionInput = {
  /** De optie waarop de klant klikt. */
  optionId: string;
  /** De waarde die de klant wil kiezen. */
  value: string;
  /** De huidige samenstelling. */
  configuration: Record<string, unknown>;
  /** Alle opties van het product. */
  options: OptionForResolution[];
  /**
   * Ook een zichtbare optie samen met een productieveld proberen. Duur: alleen
   * bij een echte klik, niet in de filter die voor elke waarde draait (dat
   * blokkeerde de pagina 1 tot 3 seconden per klik).
   */
  pairVisibleWithDerived?: boolean;
  /**
   * Beantwoordt of een waarde uitgesloten is binnen een samenstelling. De
   * configurator heeft deze functie al; hij wordt hier alleen doorgegeven.
   */
  isExcluded: (optionId: string, value: string, configuration: Record<string, unknown>) => boolean;
};

// These are supplier execution choices, including fields hidden from customers.
// A hidden customer finish/material is still protected unless explicitly listed.
const derivedIds = new Set([
  'printingmethod', 'printing_method', 'printtype', 'clean_cut', 'sheet_size',
  'plano_delivery', 'folded', 'creasing', 'variable_creasing_line', 'die_cut',
  'bundle', 'standard_bundle', 'sealed', 'delivery', 'packaging_extras',
  'customer_packing_option',
]);

/**
 * Zoekt de kleinste wijziging waarmee de gewenste keuze geldig wordt.
 *
 * Geeft een patch terug die naast de gewenste waarde toegepast moet worden, of
 * `null` als er geen geldige combinatie bestaat. Een lege patch betekent dat
 * de keuze zonder aanpassing al kan.
 *
 * Eerst zoeken we een oplossing met EEN wijziging. Sommige productiekeuzes zijn
 * echter technisch gekoppeld: afgeronde hoeken op visitekaartjes vereisen
 * bijvoorbeeld zowel een andere drukmethode als een compatibel printtype. Voor
 * zulke afgeleide productievelden proberen we daarna begrensd twee wijzigingen.
 * Primaire keuzes zoals materiaal en formaat worden nooit samen stilzwijgend
 * omgezet; die blijven onder de bestaande een-wijzigingregel vallen.
 */
export function resolveExcludedSelection(input: ResolutionInput): Record<string, string> | null {
  const { optionId, value, configuration, options, isExcluded, pairVisibleWithDerived = false } = input;

  const gewenst = { ...configuration, [optionId]: value };
  if (!isExcluded(optionId, value, configuration)) return {};

  const candidates = options.filter((optie) =>
    Boolean(optie?.id) && optie.id !== optionId && (!optie.hidden || derivedIds.has(optie.id)) && !optie.locked && Array.isArray(optie.values) && optie.values.length > 0
  );
  const trySingleOption = (optie: OptionForResolution) => {
    const huidige = String(gewenst[optie.id] ?? '');
    for (const kandidaat of optie.values || []) {
      const kandidaatWaarde = String(kandidaat?.value ?? '');
      if (!kandidaatWaarde || kandidaatWaarde === huidige) continue;

      const aangepast = { ...gewenst, [optie.id]: kandidaatWaarde };
      if (isExcluded(optionId, value, aangepast)) continue;
      if (isExcluded(optie.id, kandidaatWaarde, aangepast)) continue;
      return { [optie.id]: kandidaatWaarde };
    }
    return null;
  };

  // Technische velden eerst: verander liever de automatisch gekozen
  // productiemethode dan het materiaal of formaat dat de klant bewust koos.
  const derivedCandidates = candidates.filter((optie) => derivedIds.has(optie.id));
  for (const optie of derivedCandidates) {
    const patch = trySingleOption(optie);
    if (patch) return patch;
  }

  // Begrensde tweestapsoplossing, uitsluitend voor gekoppelde technische
  // productievelden. Hiermee blijft de zoekruimte klein en voorspelbaar.
  for (let firstIndex = 0; firstIndex < derivedCandidates.length; firstIndex += 1) {
    const firstOption = derivedCandidates[firstIndex];
    const firstCurrent = String(gewenst[firstOption.id] ?? '');
    for (const firstValue of firstOption.values || []) {
      const first = String(firstValue?.value ?? '');
      if (!first || first === firstCurrent) continue;

      for (let secondIndex = firstIndex + 1; secondIndex < derivedCandidates.length; secondIndex += 1) {
        const secondOption = derivedCandidates[secondIndex];
        const secondCurrent = String(gewenst[secondOption.id] ?? '');
        for (const secondValue of secondOption.values || []) {
          const second = String(secondValue?.value ?? '');
          if (!second || second === secondCurrent) continue;

          const aangepast = {
            ...gewenst,
            [firstOption.id]: first,
            [secondOption.id]: second,
          };
          if (isExcluded(optionId, value, aangepast)) continue;
          if (isExcluded(firstOption.id, first, aangepast)) continue;
          if (isExcluded(secondOption.id, second, aangepast)) continue;
          return { [firstOption.id]: first, [secondOption.id]: second };
        }
      }
    }
  }

  for (const optie of candidates) {
    if (derivedIds.has(optie.id)) continue;
    const patch = trySingleOption(optie);
    if (patch) return patch;
  }

  // Een productsoortkiezer (briefpapier / enveloppen / visitekaartjes) bepaalt
  // de hele rest van de samenstelling. Daar mogen alle botsende opties
  // tegelijk meebewegen; anders bleef bij het huisstijlpakket alleen de
  // startsoort over, omdat enveloppen vier wijzigingen tegelijk vragen.
  if (productTypeSelectorIds.has(optionId)) {
    const patch = resolveProductTypeSwitch(input, candidates);
    if (patch) return patch;
  }

  // Een zichtbare optie die alleen met een andere productiemethode kan, zoals
  // Roughmark bij muursticker (kisscut kan niet op Quick Stick, Roughmark
  // niet met latex). Een zichtbare keuze plus een technisch veld, begrensd.
  if (!pairVisibleWithDerived) return null;
  for (const optie of candidates) {
    if (derivedIds.has(optie.id)) continue;
    const huidige = String(gewenst[optie.id] ?? '');
    for (const kandidaat of optie.values || []) {
      const kandidaatWaarde = String(kandidaat?.value ?? '');
      if (!kandidaatWaarde || kandidaatWaarde === huidige) continue;
      for (const afgeleid of derivedCandidates) {
        const afgeleidHuidig = String(gewenst[afgeleid.id] ?? '');
        for (const afgeleideWaarde of afgeleid.values || []) {
          const tweede = String(afgeleideWaarde?.value ?? '');
          if (!tweede || tweede === afgeleidHuidig) continue;
          const aangepast = { ...gewenst, [optie.id]: kandidaatWaarde, [afgeleid.id]: tweede };
          if (isExcluded(optionId, value, aangepast)) continue;
          if (isExcluded(optie.id, kandidaatWaarde, aangepast)) continue;
          if (isExcluded(afgeleid.id, tweede, aangepast)) continue;
          return { [optie.id]: kandidaatWaarde, [afgeleid.id]: tweede };
        }
      }
    }
  }

  return null;
}

/**
 * Opties die een andere productsoort binnen hetzelfde leveranciersproduct
 * kiezen. Alleen bij een klik op zo'n optie mogen meerdere primaire keuzes
 * (maat, materiaal, sluiting ...) samen omgezet worden; een klik op een maat
 * zet de productsoort nooit stilzwijgend om.
 */
const productTypeSelectorIds = new Set(['product_type']);

/** Bovengrens voor het aantal uitsluitvragen per productsoortwissel. */
const PRODUCT_TYPE_SWITCH_BUDGET = 4000;

/**
 * Zoekt een volledige geldige samenstelling voor een nieuwe productsoort.
 *
 * Diepte-eerst over de overige opties, waarbij elke optie eerst haar huidige
 * waarde houdt. Nog niet ingevulde zichtbare opties staan tijdens het zoeken
 * op '' : een lege waarde laat geen enkele uitsluitregel vuren, zodat alleen
 * regels tussen al gekozen opties meetellen. Verborgen opties houden hun
 * waarde (een lege verborgen waarde telt behoudend als botsend) en komen als
 * eerste aan de beurt. De eindsamenstelling wordt in zijn geheel gecontroleerd.
 */
function resolveProductTypeSwitch(
  input: ResolutionInput,
  candidates: OptionForResolution[],
): Record<string, string> | null {
  const { optionId, value, configuration, isExcluded } = input;
  const order = [
    ...candidates.filter((optie) => optie.hidden && !productTypeSelectorIds.has(optie.id)),
    ...candidates.filter((optie) => !optie.hidden && !productTypeSelectorIds.has(optie.id)),
  ];
  // Andere productsoortkiezers en vergrendelde of verborgen niet-afgeleide
  // opties blijven ongewijzigd staan.
  const working: Record<string, unknown> = { ...configuration, [optionId]: value };
  for (const optie of order) {
    if (!optie.hidden) working[optie.id] = '';
  }

  let budget = PRODUCT_TYPE_SWITCH_BUDGET;
  const excluded = (id: string, waarde: string) => {
    budget -= 1;
    return isExcluded(id, waarde, working);
  };

  const search = (index: number): boolean => {
    if (budget <= 0) return false;
    if (index === order.length) {
      return !isExcluded(optionId, value, working)
        && order.every((optie) => !isExcluded(optie.id, String(working[optie.id] ?? ''), working));
    }
    const optie = order[index];
    const huidige = String(configuration[optie.id] ?? '');
    const waarden = (optie.values || [])
      .map((entry) => String(entry?.value ?? ''))
      .filter(Boolean);
    const volgorde = waarden.includes(huidige)
      ? [huidige, ...waarden.filter((waarde) => waarde !== huidige)]
      : waarden;
    // Een optie zonder huidige waarde mag leeg blijven als niets anders kan.
    if (!huidige && volgorde.length === 0) return search(index + 1);
    for (const waarde of volgorde) {
      if (budget <= 0) return false;
      working[optie.id] = waarde;
      if (excluded(optionId, value) || excluded(optie.id, waarde)) continue;
      if (search(index + 1)) return true;
    }
    working[optie.id] = optie.hidden ? configuration[optie.id] : '';
    return false;
  };

  if (!search(0)) return null;

  const patch: Record<string, string> = {};
  for (const optie of order) {
    const next = String(working[optie.id] ?? '');
    if (next !== String(configuration[optie.id] ?? '')) patch[optie.id] = next;
  }
  return patch;
}

/**
 * Kan de klant deze waarde kiezen, eventueel nadat een botsende optie
 * meebeweegt? Zo niet, dan hoort hij niet in de lijst te staan.
 */
export function isSelectableWithResolution(input: ResolutionInput): boolean {
  return resolveExcludedSelection(input) !== null;
}
